import { faPlus, faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import { useMemo } from "react";
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from "react-hook-form";

import { Button } from "src/base-components/Button";
import { Icon } from "src/base-components/Icon";
import { Pill } from "src/base-components/Pill";
import { Select } from "src/base-components/Select";
import {
  SortableField,
  SortableFieldsContext,
} from "src/base-components/schemaKit/SortableFields";
import {
  CaseTemplateFormValues,
  CaseTemplateTaskFormValue,
} from "src/cases/templates/caseTemplateFormUtils";
import { useCheckFieldPermission } from "src/cases/templates/useCheckFieldPermission";
import { useIdKeyMapping } from "src/cases/templates/useIdKeyMapping";
import {
  buildStatusKey,
  FlattenedStatusValue,
} from "src/cases/templates/utils";
import { EmptyState } from "src/design-system/EmptyState";

export const CaseTaskSelector: React.FC<{
  statusValue: FlattenedStatusValue;
}> = ({ statusValue }) => {
  const hasPermission = useCheckFieldPermission();
  const statusKey = buildStatusKey(statusValue);
  const { createWorkflowTask } = useIdKeyMapping();
  const { setValue } = useFormContext<CaseTemplateFormValues>();

  const tasks = useWatch<CaseTemplateFormValues, "attributes.case_tasks">({
    name: "attributes.case_tasks",
  });

  const {
    fields: selectedTasks,
    append,
    remove,
    move,
  } = useFieldArray<
    CaseTemplateFormValues,
    `attributes.workflow_rules.${string}.workflow_tasks_keys`,
    "_id"
  >({
    name: `attributes.workflow_rules.${statusKey}.workflow_tasks_keys`,
    keyName: "_id",
  });

  const canEditWorkflowTasks = hasPermission(
    "edit",
    `attributes.workflow_rules.${statusKey}.workflow_tasks_keys`,
  );

  const isAddTaskDisabled =
    selectedTasks.length >= 20 || selectedTasks.length >= (tasks?.length ?? 0);

  if (!selectedTasks.length) {
    return (
      <EmptyState
        actionButton={{
          text: "Add task",
          icon: faPlus,
          disabled: isAddTaskDisabled || !canEditWorkflowTasks,
          onClick: () => append(createWorkflowTask("")),
        }}
        dataLoc="empty-state-task-config"
        description="Configure which tasks reviewers should see when cases are in this status."
        headline="No tasks configured yet"
        icon={faPlus}
      />
    );
  }

  return (
    <div className="flex flex-col gap-y-3">
      <div className="rounded-lg bg-gray-50 p-4">
        <SortableFieldsContext
          items={selectedTasks.map((task) => task._id)}
          onChangeOrder={({ oldIndex, newIndex }) => move(oldIndex, newIndex)}
        >
          <div className="flex flex-col gap-y-3">
            {selectedTasks.map((task, index) => (
              <SortableField key={task._id} id={task._id}>
                <div className="flex items-center gap-x-2">
                  <div className="flex-1">
                    <Controller
                      name={`attributes.workflow_rules.${statusKey}.workflow_tasks_keys.${index}.task_key`}
                      render={({ field }) => (
                        <TaskSelect
                          currentIndex={index}
                          dataLoc={`task-select-${index}`}
                          disabled={!canEditWorkflowTasks}
                          selected={field.value}
                          statusKey={statusKey}
                          tasks={tasks}
                          onSelect={(taskKey) => {
                            const workflowTask = createWorkflowTask(taskKey);
                            field.onChange(workflowTask.task_key);
                            // Also update the _internalId field
                            const internalIdPath =
                              `attributes.workflow_rules.${statusKey}.workflow_tasks_keys.${index}._internalId` as const;
                            setValue(internalIdPath, workflowTask._internalId);
                          }}
                        />
                      )}
                    />
                  </div>
                  <Icon
                    color="text-gray-500 hover:text-gray-800"
                    dataLoc={`task-delete-${index}`}
                    disabled={!canEditWorkflowTasks}
                    icon={faTrashCan}
                    size="xs"
                    onClick={() => remove(index)}
                  />
                </div>
              </SortableField>
            ))}
          </div>
        </SortableFieldsContext>
      </div>

      {canEditWorkflowTasks && (
        <div>
          <Button
            dataLoc="add-task-button"
            disabled={isAddTaskDisabled}
            iconLeft={faPlus}
            size="sm"
            variant="secondary"
            onClick={() => append(createWorkflowTask(""))}
          >
            Add task
          </Button>
        </div>
      )}
    </div>
  );
};

const TaskSelect: React.FC<{
  currentIndex: number;
  dataLoc?: string;
  disabled?: boolean;
  selected: string;
  statusKey: string;
  tasks: CaseTemplateTaskFormValue[] | undefined;
  onSelect: (value: string) => void;
}> = ({
  currentIndex,
  dataLoc,
  disabled,
  selected,
  statusKey,
  tasks,
  onSelect,
}) => {
  const { isSameTask } = useIdKeyMapping();
  const allSelectedTasks = useWatch<
    CaseTemplateFormValues,
    `attributes.workflow_rules.${string}.workflow_tasks_keys`
  >({
    name: `attributes.workflow_rules.${statusKey}.workflow_tasks_keys`,
  });

  const taskOptions = useMemo(() => {
    if (!tasks) return [];

    const isTaskAvailable = (taskKey: string) => {
      if (!taskKey) return false;
      if (taskKey === selected) return true;

      const task = tasks.find((t) => t.key === taskKey);
      if (!task?._internalId) return false;

      const candidateTask = {
        task_key: taskKey,
        order: 0,
        _internalId: task._internalId,
      };

      return !allSelectedTasks?.some(
        (task, idx) => idx !== currentIndex && isSameTask(task, candidateTask),
      );
    };

    return tasks
      .filter((task) => isTaskAvailable(task.key))
      .map((task) => ({
        key: task.key,
        value: (
          <Pill size="sm" variant="gray">
            <Pill.Text>{task.display_name}</Pill.Text>
          </Pill>
        ),
      }));
  }, [tasks, selected, allSelectedTasks, currentIndex, isSameTask]);

  return (
    <Select
      dataLoc={dataLoc}
      disabled={!tasks || disabled}
      options={taskOptions}
      placeholder="Select a task"
      value={selected}
      onChange={onSelect}
    />
  );
};
